
import { supabase } from '@/integrations/supabase/client';

export interface PageToSeed {
  title: string;
  slug: string;
  content: string;
  meta_title: string;
  meta_description: string;
  published: boolean;
}

export const defaultPages: PageToSeed[] = [
  {
    title: 'About Us',
    slug: 'about',
    content: `
      <div class="space-y-6">
        <h2 class="text-3xl font-bold text-gray-900 mb-4">Welcome to GameHub</h2>
        <p class="text-lg text-gray-700 leading-relaxed">
          GameHub is your ultimate destination for free online games. We're passionate about bringing you the best gaming experience right in your browser, without any downloads or installations required.
        </p>
        
        <h3 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Mission</h3>
        <p class="text-gray-700 leading-relaxed">
          Our mission is to provide a comprehensive platform where gamers of all ages can discover, play, and enjoy thousands of high-quality games across various categories. From action-packed adventures to mind-bending puzzles, we curate the best games to ensure endless entertainment.
        </p>
        
        <h3 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">What We Offer</h3>
        <ul class="list-disc list-inside text-gray-700 space-y-2">
          <li>Thousands of free online games across multiple categories</li>
          <li>Regular updates with new and trending games</li>
          <li>User-friendly interface for easy game discovery</li>
          <li>Mobile-responsive design for gaming on any device</li>
          <li>No registration required for most games</li>
          <li>Safe and secure gaming environment</li>
        </ul>
        
        <h3 class="text-2xl font-semibold text-gray-900 mt-8 mb-4">Our Team</h3>
        <p class="text-gray-700 leading-relaxed">
          We're a dedicated team of gaming enthusiasts, developers, and content curators who work tirelessly to bring you the best gaming experience. Our team carefully selects and tests each game to ensure quality and entertainment value.
        </p>
        
        <div class="bg-blue-50 p-6 rounded-lg mt-8">
          <p class="text-blue-800 text-center font-medium">
            Join millions of players worldwide and discover your next favorite game at GameHub!
          </p>
        </div>
      </div>
    `,
    meta_title: 'About GameHub - Your Ultimate Gaming Destination',
    meta_description: 'Learn about GameHub, your premier destination for free online games. Discover our mission, what we offer, and meet the team behind your favorite gaming platform.',
    published: true
  },
  {
    title: 'Contact Us',
    slug: 'contact',
    content: `
      <div class="space-y-6">
        <h2 class="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
        <p class="text-lg text-gray-700 leading-relaxed">
          We'd love to hear from you! Whether you have questions, suggestions, or need support, our team is here to help.
        </p>
        
        <div class="grid md:grid-cols-2 gap-8 mt-8">
          <div>
            <h3 class="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div class="space-y-4">
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mt-1">
                  <span class="text-white text-xs">📧</span>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Email</h4>
                  <p class="text-gray-600">support@gamehub.com</p>
                </div>
              </div>
              
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center mt-1">
                  <span class="text-white text-xs">💬</span>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Live Chat</h4>
                  <p class="text-gray-600">Available 24/7 for immediate assistance</p>
                </div>
              </div>
              
              <div class="flex items-start space-x-3">
                <div class="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center mt-1">
                  <span class="text-white text-xs">🕒</span>
                </div>
                <div>
                  <h4 class="font-medium text-gray-900">Response Time</h4>
                  <p class="text-gray-600">We typically respond within 24 hours</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h3 class="text-2xl font-semibold text-gray-900 mb-4">What You Can Contact Us About</h3>
            <ul class="space-y-2 text-gray-700">
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>Technical support and bug reports</span>
              </li>
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>Game suggestions and requests</span>
              </li>
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>Partnership and collaboration opportunities</span>
              </li>
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>Advertising and promotional inquiries</span>
              </li>
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>General feedback and suggestions</span>
              </li>
              <li class="flex items-center space-x-2">
                <span class="text-blue-600">•</span>
                <span>Account-related questions</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div class="bg-gray-50 p-6 rounded-lg mt-8">
          <h3 class="text-xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h3>
          <div class="space-y-4">
            <div>
              <h4 class="font-medium text-gray-900">Q: Are all games really free?</h4>
              <p class="text-gray-600">A: Yes! All games on GameHub are completely free to play. No hidden fees or premium subscriptions required.</p>
            </div>
            <div>
              <h4 class="font-medium text-gray-900">Q: Do I need to create an account?</h4>
              <p class="text-gray-600">A: No account is required for most games. You can start playing immediately!</p>
            </div>
            <div>
              <h4 class="font-medium text-gray-900">Q: Can I play games on mobile devices?</h4>
              <p class="text-gray-600">A: Absolutely! Our platform is fully responsive and works great on phones, tablets, and computers.</p>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-8 p-6 bg-blue-50 rounded-lg">
          <p class="text-blue-800 font-medium">
            Thank you for choosing GameHub! We appreciate your feedback and look forward to hearing from you.
          </p>
        </div>
      </div>
    `,
    meta_title: 'Contact GameHub - Get Support & Share Feedback',
    meta_description: 'Contact GameHub for support, feedback, or inquiries. Get in touch with our team for technical support, game suggestions, partnerships, and more.',
    published: true
  },
  {
    title: 'Privacy Policy',
    slug: 'privacy',
    content: `
      <div class="space-y-8">
        <div class="text-center mb-8">
          <p class="text-lg text-gray-600">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <p class="text-sm text-gray-500 mt-4">Last updated: June 15, 2025</p>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
          <div class="space-y-4 text-gray-700">
            <div>
              <h3 class="font-semibold text-gray-900 mb-2">Automatically Collected Information</h3>
              <ul class="list-disc list-inside space-y-1 text-gray-600">
                <li>IP address and general location information</li>
                <li>Browser type and version</li>
                <li>Device information (type, operating system)</li>
                <li>Pages visited and time spent on our site</li>
                <li>Referring website information</li>
              </ul>
            </div>
            <div>
              <h3 class="font-semibold text-gray-900 mb-2">Information You Provide</h3>
              <ul class="list-disc list-inside space-y-1 text-gray-600">
                <li>Account information (if you create an account)</li>
                <li>Email address for newsletters (optional)</li>
                <li>Game preferences and saved progress</li>
                <li>Comments or feedback you submit</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
          <ul class="list-disc list-inside space-y-2 text-gray-600">
            <li>To provide and maintain our gaming services</li>
            <li>To improve user experience and website functionality</li>
            <li>To analyze usage patterns and optimize performance</li>
            <li>To send occasional updates about new games (if subscribed)</li>
            <li>To respond to your inquiries and provide customer support</li>
            <li>To prevent fraud and ensure website security</li>
            <li>To comply with legal obligations</li>
          </ul>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Data Protection & Security</h2>
          <p class="text-gray-700 mb-4">We implement appropriate security measures to protect your personal information:</p>
          <ul class="list-disc list-inside space-y-2 text-gray-600">
            <li>SSL encryption for data transmission</li>
            <li>Secure database storage with access controls</li>
            <li>Regular security audits and updates</li>
            <li>Limited access to personal data by authorized personnel only</li>
            <li>Data backup and recovery procedures</li>
          </ul>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
          <p class="text-gray-700 mb-4">You have the following rights regarding your personal information:</p>
          <ul class="list-disc list-inside space-y-2 text-gray-600">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Objection:</strong> Object to certain types of processing</li>
            <li><strong>Restriction:</strong> Request limitation of processing</li>
          </ul>
        </div>

        <div class="bg-gray-50 p-6 rounded-lg">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
          <p class="text-gray-700 mb-4">
            If you have questions about this privacy policy or want to exercise your rights, please contact us:
          </p>
          <p class="text-gray-700"><strong>Email:</strong> privacy@gamehub.com</p>
          <p class="text-gray-700"><strong>Response Time:</strong> We will respond within 30 days</p>
        </div>
      </div>
    `,
    meta_title: 'Privacy Policy - GameHub',
    meta_description: 'Learn about how GameHub protects your privacy and handles your personal information.',
    published: true
  },
  {
    title: 'Terms of Service',
    slug: 'terms',
    content: `
      <div class="space-y-8">
        <div class="text-center mb-8">
          <p class="text-lg text-gray-600">
            Please read these terms carefully before using GameHub. By using our services, you agree to these terms.
          </p>
          <p class="text-sm text-gray-500 mt-4">Last updated: June 15, 2025</p>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Acceptance of Terms</h2>
          <p class="text-gray-700">
            By accessing and using GameHub, you accept and agree to be bound by the terms and provision of this agreement. 
            If you do not agree to abide by these terms, please do not use this service.
          </p>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Use of Service</h2>
          <div class="space-y-4">
            <div>
              <h3 class="font-semibold text-gray-900 mb-2">Permitted Use</h3>
              <ul class="list-disc list-inside space-y-1 text-gray-600">
                <li>Access and play games for personal, non-commercial use</li>
                <li>Create an account to save game progress and preferences</li>
                <li>Share games with friends through provided sharing features</li>
                <li>Provide feedback and suggestions for improvement</li>
              </ul>
            </div>
            <div>
              <h3 class="font-semibold text-gray-900 mb-2">Prohibited Use</h3>
              <ul class="list-disc list-inside space-y-1 text-gray-600">
                <li>Violate any applicable local, state, national, or international law</li>
                <li>Transmit or distribute viruses, malware, or other harmful code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated scripts or bots to access the service</li>
                <li>Copy, modify, or distribute our games without permission</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">User Accounts</h2>
          <p class="text-gray-700 mb-4">
            When you create an account with us, you must provide information that is accurate, 
            complete, and current at all times.
          </p>
          <div>
            <h3 class="font-semibold text-gray-900 mb-2">Account Responsibilities</h3>
            <ul class="list-disc list-inside space-y-1 text-gray-600">
              <li>Maintain the security of your password and account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Provide accurate and truthful information</li>
            </ul>
          </div>
        </div>

        <div class="bg-white p-6 rounded-lg border">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
          <p class="text-gray-700 mb-4">
            The service and its original content, features, and functionality are and will remain 
            the exclusive property of GameHub and its licensors.
          </p>
          <ul class="list-disc list-inside space-y-1 text-gray-600">
            <li>All games, graphics, and content are protected by copyright</li>
            <li>GameHub trademarks and logos are our exclusive property</li>
            <li>Website design and functionality are protected intellectual property</li>
          </ul>
        </div>

        <div class="bg-orange-50 p-6 rounded-lg border border-orange-200">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Disclaimers</h2>
          <p class="text-orange-800 font-semibold mb-2">IMPORTANT DISCLAIMER</p>
          <p class="text-orange-700 text-sm mb-4">
            The information on this website is provided on an "as is" basis. To the fullest extent 
            permitted by law, GameHub excludes all representations, warranties, conditions, and terms.
          </p>
          <ul class="list-disc list-inside space-y-1 text-gray-600">
            <li>We do not guarantee uninterrupted or error-free service</li>
            <li>Games may be temporarily unavailable for maintenance</li>
            <li>We reserve the right to modify or discontinue services</li>
          </ul>
        </div>

        <div class="bg-gray-50 p-6 rounded-lg">
          <h2 class="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <p class="text-gray-700 mb-4">
            If you have any questions about these Terms of Service, please contact us:
          </p>
          <p class="text-gray-700"><strong>Email:</strong> legal@gamehub.com</p>
          <p class="text-gray-700"><strong>Response Time:</strong> We will respond within 7 business days</p>
        </div>
      </div>
    `,
    meta_title: 'Terms of Service - GameHub',
    meta_description: 'Read our terms of service and user agreement for using GameHub gaming platform.',
    published: true
  }
];

export const seedDefaultPages = async (): Promise<{ success: boolean; message: string }> => {
  try {
    for (const page of defaultPages) {
      // Check if page already exists
      const { data: existingPage } = await supabase
        .from('pages')
        .select('id')
        .eq('slug', page.slug)
        .maybeSingle();

      if (!existingPage) {
        const { error } = await supabase
          .from('pages')
          .insert([page]);

        if (error) {
          throw error;
        }
      }
    }

    return {
      success: true,
      message: 'Default pages created successfully!'
    };
  } catch (error: any) {
    return {
      success: false,
      message: `Failed to create pages: ${error.message}`
    };
  }
};
